import { useQuery } from 'react-query';
import { getJobFolderId, SHAREPOINT_API } from 'services/api/sharepoint';

interface UseSharePointFilesProps {
  jobNumbers: string[];
  companyNames: string[];
  enabled?: boolean;
}

export const useSharePointFiles = ({
  jobNumbers,
  companyNames,
  enabled = true
}: UseSharePointFilesProps) => {
  return useQuery(
    ['sharepoint-job-images', jobNumbers, companyNames],
    async () => {
      if (!jobNumbers.length || !companyNames.length) return [];

      try {
        const { results } = await getJobFolderId(jobNumbers, companyNames);
        if (!results?.length || !results[0].found) return [];

        const jobFolderId = results[0].folders[0]?.id;
        if (!jobFolderId) return [];

        // Get the job folder contents
        const folderDetails = await SHAREPOINT_API.GET_FOLDER_DETAILS(
          jobFolderId
        );

        // Try to find "Images" or "images" folder
        const imagesFolder = folderDetails.folders?.find(
          f => f.name.toLowerCase() === 'images'
        );

        if (imagesFolder) {
          const imagesFolderDetails = await SHAREPOINT_API.GET_FOLDER_DETAILS(
            imagesFolder.id
          );
          return imagesFolderDetails.files || [];
        }

        // Fallback: if no Images folder, look for images in the root job folder
        return (folderDetails.files || []).filter(
          f =>
            f.fileType?.startsWith('image/') ||
            f.name.toLowerCase().endsWith('.jpg') ||
            f.name.toLowerCase().endsWith('.jpeg') ||
            f.name.toLowerCase().endsWith('.png') ||
            f.name.toLowerCase().endsWith('.gif')
        );
      } catch (error) {
        console.error('Error fetching SharePoint files:', error);
        return [];
      }
    },
    {
      enabled: enabled && jobNumbers.length > 0 && companyNames.length > 0,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1
    }
  );
};
